// What an article page links to below its body: the listing pages its topics
// and cities point at, and a few sibling articles. Pure functions over data the
// page already has (the article's own `topics` / `cities` codes, the locale's
// article list seed and the footer's exported-page lists), so the build render
// and the client's hydration render produce the same links.

import { articleCities } from '@/lib/i18n/article-content';
import type { Locale } from '@todo/core/i18n/locales';
import type { FooterCityLinks } from '@todo/core/list-queries';
import { cityPageLink, hubLink, type ListingLink, type ListingPageKind } from '@/lib/listing-links';
import type { Article } from '@todo/core/sanity/queries';

type Translate = (key: string) => string;

/** "More articles" cards — one row of the RelatedList grid. */
export const RELATED_ARTICLES_LIMIT = 3;

/** Enough to cover a main topic across several cities without turning into a link farm. */
const MAX_LISTING_LINKS = 8;

// Topic code → the per-city page type that sells it. `food` and `practical`
// have no catalogue of their own; those articles still get the city guides.
const TOPIC_PAGES: Record<string, ListingPageKind> = {
  boats: 'boatRental',
  wine: 'wineTours',
  tours: 'cityTours',
  festivals: 'cityEvents',
  seasonal: 'cityEvents',
  culture: 'cityEvents',
};

/**
 * Listing pages for an article, main topic first: each topic's per-city page
 * for the article's cities that have one exported (`pages` — the same lists the
 * footer links to, so nothing here can 404), else that topic's hub; then the
 * city guides, which every city has. Anchor text is each page's own title —
 * the phrase it's meant to rank for.
 */
export function articleListingLinks(
  article: Pick<Article, 'topics' | 'cities'>,
  pages: FooterCityLinks,
  locale: Locale,
  t: Translate,
): ListingLink[] {
  const cityIds = articleCities(article.cities).map((c) => c.id);
  const links: ListingLink[] = [];
  const seenKinds = new Set<ListingPageKind>();

  for (const topic of article.topics ?? []) {
    const kind = TOPIC_PAGES[topic];
    if (!kind || seenKinds.has(kind)) continue;
    seenKinds.add(kind);
    const withPage = cityIds.filter((id) => pages[kind].includes(id));
    if (withPage.length > 0) links.push(...withPage.map((id) => cityPageLink(kind, id, locale, t)));
    else links.push(hubLink(kind, locale, t));
  }

  for (const id of cityIds) {
    links.push({
      key: `city:${id}`,
      href: { pathname: '/[lang]/[city]', params: { lang: locale, city: id } },
      label: t(`cityPages.${id}.title`),
    });
  }

  return links.slice(0, MAX_LISTING_LINKS);
}

function overlap(a: string[] | null, b: string[] | null): number {
  if (!a || !b) return 0;
  return a.filter((x) => b.includes(x)).length;
}

/**
 * Sibling articles ranked by shared topics (a shared main topic counts extra),
 * then shared cities. Ties keep the list's own order — newest first — and an
 * article with nothing in common still fills the row, so every article page
 * links on to others.
 */
export function relatedArticles(
  article: Pick<Article, '_id' | 'topics' | 'cities'>,
  all: Article[],
  limit = RELATED_ARTICLES_LIMIT,
): Article[] {
  const mainTopic = article.topics?.[0];
  const score = (other: Article) =>
    overlap(article.topics, other.topics) * 2 +
    (mainTopic && other.topics?.[0] === mainTopic ? 1 : 0) +
    overlap(article.cities, other.cities);

  return all
    .filter((other) => other._id !== article._id)
    .map((other, index) => ({ other, index, score: score(other) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, limit)
    .map(({ other }) => other);
}
